import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { useAuthStore } from '@/stores/auth-store';

// Layouts
import PublicLayout from '@/components/layout/PublicLayout';
import AccountLayout from '@/components/layout/AccountLayout';
import AdminLayout from '@/components/layout/AdminLayout';

// Public pages
import Home from '@/pages/public/Home';
import Shop from '@/pages/public/Shop';
import ProductDetail from '@/pages/public/ProductDetail';
import SearchPage from '@/pages/public/SearchPage';
import BlogList from '@/pages/public/BlogList';
import BlogPost from '@/pages/public/BlogPost';
import NotFound from '@/pages/public/NotFound';
import DynamicPage from '@/pages/public/DynamicPage';
import Copyright from '@/pages/public/Copyright';
import LocalGuidePage from '@/pages/public/LocalGuidePage';
// Auth pages
import Login from '@/pages/public/Login';
import Signup from '@/pages/public/Signup';
import AuthCallback from '@/pages/public/AuthCallback';
import MarketplaceCallback from '@/pages/public/MarketplaceCallback';
import ForgotPassword from '@/pages/public/ForgotPassword';
import ResetPassword from '@/pages/public/ResetPassword';
import EmailPreferences from '@/pages/public/EmailPreferences';

// Checkout
import Cart from '@/pages/checkout/Cart';
import Checkout from '@/pages/checkout/Checkout';
import OrderConfirmation from '@/pages/checkout/OrderConfirmation';

// Account pages
import AccountDashboard from '@/pages/account/Dashboard';
import AccountOrders from '@/pages/account/Orders';
import AccountOrderDetail from '@/pages/account/OrderDetail';
import AccountAddresses from '@/pages/account/Addresses';
import AccountWishlist from '@/pages/account/Wishlist';
import AccountProfile from '@/pages/account/Profile';
import AccountReviews from '@/pages/account/Reviews';
import AccountBilling from '@/pages/account/Billing';
// Admin pages
import AdminDashboard from '@/pages/admin/DashboardHome';
import AdminProducts from '@/pages/admin/Products';
import AdminProductEditor from '@/pages/admin/ProductEditor';
import AdminCategories from '@/pages/admin/Categories';
import AdminOrders from '@/pages/admin/Orders';
import AdminOrderDetail from '@/pages/admin/OrderDetail';
import AdminCarts from '@/pages/admin/Carts';
import AdminCustomers from '@/pages/admin/Customers';
import AdminCustomerDetail from '@/pages/admin/CustomerDetail';
import AdminInventory from '@/pages/admin/Inventory';
import AdminPages from '@/pages/admin/Pages';
import AdminPageEditor from '@/pages/admin/PageEditor';
import AdminBlog from '@/pages/admin/Blog';
import AdminBlogEditor from '@/pages/admin/BlogEditor';
import AdminBlogCategories from '@/pages/admin/BlogCategories';
import AdminBlogAgent from '@/pages/admin/BlogAgent';
import AdminHomepageBuilder from '@/pages/admin/HomepageBuilder';
import AdminMediaLibrary from '@/pages/admin/MediaLibrary';
import AdminDesignerStart from '@/pages/admin/DesignerStart';
import AdminCoupons from '@/pages/admin/Coupons';
import AdminCouponEditor from '@/pages/admin/CouponEditor';
import AdminReviews from '@/pages/admin/Reviews';
import AdminReviewAnalytics from '@/pages/admin/ReviewAnalytics';
import AdminSettings from '@/pages/admin/Settings';
import AdminShipping from '@/pages/admin/Shipping';
import AdminEmailPopup from '@/pages/admin/EmailPopup';
import AdminThemeCustomizer from '@/pages/admin/ThemeCustomizer';
import AdminDesignSandbox from '@/pages/admin/DesignSandbox';
import AdminDesignHistory from '@/pages/admin/DesignHistory';
import AdminDesignChecklist from '@/pages/admin/DesignChecklist';
import AdminBrandingStudio from '@/pages/admin/BrandingStudio';
import DesignPreviewBridge from '@/components/sandbox/DesignPreviewBridge';
import DesignSharePreview from '@/components/sandbox/DesignSharePreview';
import RuntimeBrand from '@/components/RuntimeBrand';
import AdminEmailSetup from '@/pages/admin/EmailSetup';
import PlatformCredentials from '@/pages/admin/PlatformCredentials';
import Integrations from '@/pages/admin/Integrations';
const IntegrationDetail = lazy(() => import('@/pages/admin/integrations/IntegrationDetail'));
import PlatformPlans from '@/pages/admin/PlatformPlans';
import PlatformPromos from '@/pages/admin/PlatformPromos';
import StoreMigrations from '@/pages/admin/StoreMigrations';
import PaymentsSettings from '@/pages/admin/PaymentsSettings';
import SecuritySettings from '@/pages/admin/SecuritySettings';
import SocialLoginSettings from '@/pages/admin/SocialLoginSettings';
import SiteWizard from '@/pages/admin/SiteWizard';
import SiteWizardProgress from '@/pages/admin/SiteWizardProgress';
import PromoteDomain from '@/pages/admin/PromoteDomain';
import Welcome from '@/pages/admin/Welcome';
import BusinessProfile from '@/pages/admin/BusinessProfile';
import PlatformSettings from '@/pages/admin/PlatformSettings';
import PlatformWorkspaces from '@/pages/admin/PlatformWorkspaces';
import PlatformWorkspaceDetail from '@/pages/admin/PlatformWorkspaceDetail';
import AgentFleetDashboard from '@/pages/admin/AgentFleetDashboard';

// Newsletter admin pages
import AdminNewsletterSubscribers from '@/pages/admin/NewsletterSubscribers';
import AdminNewsletterCampaigns from '@/pages/admin/NewsletterCampaigns';
import AdminNewsletterCampaignEditor from '@/pages/admin/NewsletterCampaignEditor';
import AdminNewsletterSchedules from '@/pages/admin/NewsletterSchedules';
import AdminSearchAnalytics from '@/pages/admin/SearchAnalytics';
import AdminFulfillment from '@/pages/admin/Fulfillment';
import AdminFulfillmentRules from '@/pages/admin/FulfillmentRules';
import AdminFulfillmentDashboard from '@/pages/admin/FulfillmentDashboard';
import AdminReturns from '@/pages/admin/Returns';
import AdminSelfExclusions from '@/pages/admin/social-casino/SelfExclusions';
import AdminSuppliers from '@/pages/admin/Suppliers';
import AdminSupplierEditor from '@/pages/admin/SupplierEditor';
import AdminSupplierOrders from '@/pages/admin/SupplierOrders';
import AdminCustomsInvoices from '@/pages/admin/AdminCustomsInvoices';
import AdminCustomsUpload from '@/pages/admin/AdminCustomsUpload';
import AdminCustomsDetail from '@/pages/admin/AdminCustomsDetail';
import AdminMarketplaceOverview from '@/pages/admin/MarketplaceOverview';
import AdminMarketplaceChannels from '@/pages/admin/MarketplaceChannels';
import AdminMarketplaceChannelEditor from '@/pages/admin/MarketplaceChannelEditor';
import AdminMarketplaceListings from '@/pages/admin/MarketplaceListings';
import AdminMarketplaceAnalytics from '@/pages/admin/MarketplaceAnalytics';
import AgentDashboard from '@/pages/admin/AgentDashboard';
import AgentChat from '@/pages/admin/AgentChat';
import AgentTasks from '@/pages/admin/AgentTasks';
import AgentTaskDetail from '@/pages/admin/AgentTaskDetail';
import AgentConfig from '@/pages/admin/AgentConfig';
import AgentNotifications from '@/pages/admin/AgentNotifications';
import AgentApprovals from '@/pages/admin/AgentApprovals';
import AgentSchedules from '@/pages/admin/AgentSchedules';
import AgentToolStats from '@/pages/admin/AgentToolStats';
import AgentScheduleDetail from '@/pages/admin/AgentScheduleDetail';
import SupplierProspects from '@/pages/admin/SupplierProspects';

// Advertising admin pages
const AddieDashboard = lazy(() => import('@/pages/admin/advertising/AddieDashboard'));
const AddieChat = lazy(() => import('@/pages/admin/advertising/AddieChat'));
const AddieSimulator = lazy(() => import('@/pages/admin/advertising/AddieSimulator'));
const AdPlatforms = lazy(() => import('@/pages/admin/advertising/AdPlatforms'));
const CampaignsList = lazy(() => import('@/pages/admin/advertising/CampaignsList'));
const CampaignEditor = lazy(() => import('@/pages/admin/advertising/CampaignEditor'));
const CampaignFolder = lazy(() => import('@/pages/admin/advertising/CampaignFolder'));
const AdLocalEvents = lazy(() => import('@/pages/admin/advertising/LocalEvents'));
const AdReviewQueue = lazy(() => import('@/pages/admin/advertising/ReviewQueue'));
const AdStayPreview = lazy(() => import('@/pages/admin/advertising/StayPreview'));
const BudgetManager = lazy(() => import('@/pages/admin/advertising/BudgetManager'));
const AddieConfig = lazy(() => import('@/pages/admin/advertising/AddieConfig'));
const AdAnalytics = lazy(() => import('@/pages/admin/advertising/AdAnalytics'));
const AssetLibrary = lazy(() => import('@/pages/admin/advertising/AssetLibrary'));
const BrandCast = lazy(() => import('@/pages/admin/advertising/BrandCast'));
const CastWizard = lazy(() => import('@/pages/admin/advertising/cast-wizard/CastWizardPage'));
const VideoStudio = lazy(() => import('@/pages/admin/advertising/VideoStudio'));
const CommercialStudio = lazy(() => import('@/pages/admin/advertising/CommercialStudio'));
const Audiences = lazy(() => import('@/pages/admin/advertising/Audiences'));
const RetargetingFunnels = lazy(() => import('@/pages/admin/advertising/RetargetingFunnels'));
const AddieTasks = lazy(() => import('@/pages/admin/advertising/AddieTasks'));
const AddieSchedules = lazy(() => import('@/pages/admin/advertising/AddieSchedules'));

// Public events
const PublicEventsList = lazy(() => import('@/pages/public/EventsList'));
const PublicEventDetail = lazy(() => import('@/pages/public/EventDetail'));

// Admin events
const AdminEventsList = lazy(() => import('@/pages/admin/events/EventsList'));
const AdminEventEditor = lazy(() => import('@/pages/admin/events/EventEditor'));
const AdminEventRegistrations = lazy(() => import('@/pages/admin/events/EventRegistrations'));

// Public membership (museum Phase 3)
const PublicMembership = lazy(() => import('@/pages/public/Membership'));
const AccountMembership = lazy(() => import('@/pages/account/Membership'));
const AdminMembershipTiers = lazy(() => import('@/pages/admin/fundraising/MembershipTiers'));

// Lodging (STR direct booking — Phase 2)
const PublicPropertiesList = lazy(() => import('@/pages/public/PropertiesList'));
const PublicPropertyDetail = lazy(() => import('@/pages/public/PropertyDetail'));
const PublicEventLandingPage = lazy(() => import('@/pages/public/EventLandingPage'));
const PublicReservationLookup = lazy(() => import('@/pages/public/ReservationLookup'));
const PublicReservationDetail = lazy(() => import('@/pages/public/ReservationDetail'));
const AccountMyTrips = lazy(() => import('@/pages/account/MyTrips'));

/** Redirect the retired /properties/:slug/book route to the property detail. */
function BookRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/properties/${slug ?? ''}`} replace />;
}
const AdminPropertiesList = lazy(() => import('@/pages/admin/properties/PropertiesList'));
const AdminPropertyEditor = lazy(() => import('@/pages/admin/properties/PropertyEditor'));
const AdminPropertyCalendar = lazy(() => import('@/pages/admin/properties/PropertyCalendar'));
const AdminMultiPropertyCalendar = lazy(() => import('@/pages/admin/properties/MultiPropertyCalendar'));
const AdminBulkOps = lazy(() => import('@/pages/admin/properties/BulkOps'));
const AdminReservationsList = lazy(() => import('@/pages/admin/reservations/ReservationsList'));
const AdminReservationDetail = lazy(() => import('@/pages/admin/reservations/ReservationDetail'));
const AdminPricingRules = lazy(() => import('@/pages/admin/PricingRules'));
const AdminChannelSync = lazy(() => import('@/pages/admin/lodging/ChannelSync'));
const AdminWelcomeScreens = lazy(() => import('@/pages/admin/lodging/WelcomeScreens'));
const WelcomeFrame = lazy(() => import('@/pages/public/WelcomeFrame'));
const AdminLodgingReviews = lazy(() => import('@/pages/admin/lodging/Reviews'));
const AdminLodgingReviewImport = lazy(() => import('@/pages/admin/lodging/ReviewImport'));
const AdminLodgingDashboard = lazy(() => import('@/pages/admin/lodging/Dashboard'));
const AdminMessagesInbox = lazy(() => import('@/pages/admin/messages/Inbox'));
const AdminTurnovers = lazy(() => import('@/pages/admin/lodging/Turnovers'));
const AdminCleaners = lazy(() => import('@/pages/admin/lodging/Cleaners'));
const AdminOwnerRez = lazy(() => import('@/pages/admin/lodging/OwnerRez'));
const CleanerLogin = lazy(() => import('@/pages/cleaner/Login'));
const CleanerVerify = lazy(() => import('@/pages/cleaner/Verify'));
const CleanerDashboard = lazy(() => import('@/pages/cleaner/Dashboard'));
const CleanerTurnoverDetail = lazy(() => import('@/pages/cleaner/TurnoverDetail'));
const CleanerHistory = lazy(() => import('@/pages/cleaner/History'));

// Fundraising (Penny) admin pages
const FundraisingDashboard = lazy(() => import('@/pages/admin/fundraising/FundraisingDashboard'));
const PennyChat = lazy(() => import('@/pages/admin/fundraising/PennyChat'));
const FundraisingOrgProfile = lazy(() => import('@/pages/admin/fundraising/OrgProfile'));
const FundraisingFunders = lazy(() => import('@/pages/admin/fundraising/Funders'));
const FundraisingApplications = lazy(() => import('@/pages/admin/fundraising/Applications'));
const FundraisingDonors = lazy(() => import('@/pages/admin/fundraising/Donors'));
const FundraisingSponsors = lazy(() => import('@/pages/admin/fundraising/Sponsors'));
const FundraisingMembers = lazy(() => import('@/pages/admin/fundraising/Members'));
const FundraisingCalendar = lazy(() => import('@/pages/admin/fundraising/Calendar'));
const PennyConfig = lazy(() => import('@/pages/admin/fundraising/PennyConfig'));

import FeatureToggles from '@/pages/admin/FeatureToggles';

// Upsell admin pages
import UpsellDashboard from '@/pages/admin/UpsellDashboard';
import UpsellAssociations from '@/pages/admin/UpsellAssociations';
import UpsellVolumeDiscounts from '@/pages/admin/UpsellVolumeDiscounts';
import UpsellBundles from '@/pages/admin/UpsellBundles';
import UpsellBundleEditor from '@/pages/admin/UpsellBundleEditor';
import UpsellBundleAnalytics from '@/pages/admin/UpsellBundleAnalytics';
import UpsellTimers from '@/pages/admin/UpsellTimers';
import UpsellEmailAutomation from '@/pages/admin/UpsellEmailAutomation';
import UpsellLoyalty from '@/pages/admin/UpsellLoyalty';
import UpsellReferrals from '@/pages/admin/UpsellReferrals';
import UpsellGiftCards from '@/pages/admin/UpsellGiftCards';

// Course pages
import CoursesCatalog from '@/pages/public/CoursesCatalog';
import CourseDetail from '@/pages/public/CourseDetail';
import Pricing from '@/pages/public/Pricing';

// SaaS marketing pages (rendered when SITE_TYPE === 'saas')
import SaasPricing from '@/pages/public/saas/SaasPricing';
import SaasFeatures from '@/pages/public/saas/SaasFeatures';
import SaasCaseStudies from '@/pages/public/saas/SaasCaseStudies';
import SaasAbout from '@/pages/public/saas/SaasAbout';
import SaasVerticalHome from '@/pages/public/saas/SaasVerticalHome';
import { VERTICALS } from '@/pages/public/saas/verticals';
import { IS_SAAS, IS_RUMIO, IS_ECOMMERCE, IS_LODGING, IS_SINGLE_PROPERTY } from '@/config/site';
import TenantMarketingPage from '@/components/TenantMarketingPage';
import MyCourses from '@/pages/account/MyCourses';
import MyDownloads from '@/pages/account/MyDownloads';
import CoursePlayer from '@/pages/account/CoursePlayer';
import Certificates from '@/pages/account/Certificates';
import AdminCourses from '@/pages/admin/Courses';
import AdminCourseEditor from '@/pages/admin/CourseEditor';
import AdminEnrollments from '@/pages/admin/Enrollments';
import AdminSubscriptionPlans from '@/pages/admin/SubscriptionPlans';
import AdminSubscriptionPromos from '@/pages/admin/SubscriptionPromos';
import AdminFoundersPricing from '@/pages/admin/FoundersPricing';
import AdminApps from '@/pages/admin/Apps';
import AdminAppDetail from '@/pages/admin/AppDetail';
import AdminAppsAuthorize from '@/pages/admin/AppsAuthorize';
import AppMountedRoute from '@/pages/AppMountedRoute';
import InviteRedirect from '@/pages/public/InviteRedirect';
import { useAppMounts } from '@/lib/apps-mounts';
import { routeBoundaryKey } from '@/lib/route-boundary-key';

// Public upsell pages
import Bundles from '@/pages/public/Bundles';
import BundleDetail from '@/pages/public/BundleDetail';
import GiftCards from '@/pages/public/GiftCards';
import TrackOrder from '@/pages/public/TrackOrder';

// Account upsell pages
import Loyalty from '@/pages/account/Loyalty';
import Referrals from '@/pages/account/Referrals';
import AccountReturns from '@/pages/account/Returns';

// Toast provider
import ToastProvider from '@/components/ui/ToastProvider';

// Error boundary
import ErrorBoundary from '@/components/ErrorBoundary';

// Maintenance mode
import MaintenanceGuard from '@/components/MaintenanceGuard';
import AuthorizedHomeRedirect from '@/components/AuthorizedHomeRedirect';

// i18n locale resolver — listens to URL changes and pushes the active locale
// through to i18next + <html lang/dir>. Side-effect-only; renders nothing.
import LocaleResolver from '@/components/LocaleResolver';

// Deploy-freshness banner (poll for new builds)
import UpdateAvailableBanner from '@/components/UpdateAvailableBanner';
import { captureAttribution } from '@/lib/attribution';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function AppInitializer({ children }: { children: React.ReactNode }) {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return <>{children}</>;
}

// Scrolls to top on route change so pages don't inherit previous scroll position
// Skips admin routes — admin layout manages its own scroll context
function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if (!pathname.startsWith('/admin')) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

// Resets ErrorBoundary on route change so a crash on one page doesn't block navigation.
// Not within an installed app's mount: that would remount (reload) the app's iframe on
// each of its own screen changes (see routeBoundaryKey).
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { data: mounts } = useAppMounts();
  const key = routeBoundaryKey(location.pathname, (mounts ?? []).map((m) => m.path_prefix));
  return <ErrorBoundary key={key}>{children}</ErrorBoundary>;
}

// Wraps the routing tree with the app-mount loader. We need the mount list
// available before rendering <Routes> so the dynamic mount routes can be
// declared as proper Route children. Mounts change rarely; the hook caches.
function RoutesWithMounts() {
  const { data: mounts } = useAppMounts();
  const mountRoutes = (mounts ?? []).map((m) => (
    <Route
      key={m.id}
      path={`${m.path_prefix}/*`}
      element={<AppMountedRoute mount={m} />}
    />
  ));
  return <AppRoutes mountRoutes={mountRoutes} />;
}

function AppRoutes({ mountRoutes }: { mountRoutes: React.ReactNode }) {
  return (
    <Routes>
            {/* Public routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              {/*
                Ecommerce routes (shop, product detail, cart, checkout,
                orders) are gated by IS_ECOMMERCE. For ecommerce site type
                this is always true; for museum tenants it follows the
                gift-shop opt-in (features.ecommerce). When off, these
                paths fall through to the catch-all NotFound below.
              */}
              {IS_ECOMMERCE && <Route path="/shop" element={<Shop />} />}
              {IS_ECOMMERCE && <Route path="/shop/:category" element={<Shop />} />}
              {IS_ECOMMERCE && <Route path="/product/:slug" element={<ProductDetail />} />}
              {!IS_LODGING && <Route path="/search" element={<SearchPage />} />}
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              {/* Bare /invite/:code → bounces to the active app's invite URL.
                  Lets apps (e.g. meal-planner) hand out short links on the
                  tenant's primary domain instead of /<mount-prefix>/invite/... */}
              <Route path="/invite/:code" element={<InviteRedirect />} />
              <Route path="/courses" element={<CoursesCatalog />} />
              <Route path="/courses/:slug" element={<CourseDetail />} />
              {/*
                SaaS marketing routes. The hardcoded Saas* components are
                Rumio-specific (platform-selling-itself copy). For any other
                SaaS tenant, render the tenant's own /pages table content via
                TenantMarketingPage. Ecommerce tenants still get <Pricing />
                on /pricing and 404 on the others.
              */}
              <Route
                path="/pricing"
                element={
                  IS_SAAS
                    ? IS_RUMIO
                      ? <SaasPricing />
                      : <TenantMarketingPage slug="pricing" fallbackTitle="Pricing" />
                    : <Pricing />
                }
              />
              {IS_SAAS && (
                <Route
                  path="/features"
                  element={IS_RUMIO ? <SaasFeatures /> : <TenantMarketingPage slug="features" fallbackTitle="Features" />}
                />
              )}
              {IS_SAAS && (
                <Route
                  path="/case-studies"
                  element={IS_RUMIO ? <SaasCaseStudies /> : <TenantMarketingPage slug="customers" fallbackTitle="Customers" />}
                />
              )}
              {IS_SAAS && (
                <Route
                  path="/about"
                  element={IS_RUMIO ? <SaasAbout /> : <TenantMarketingPage slug="about" fallbackTitle="About" />}
                />
              )}
              {/*
                rumio.world per site type: /stores, /lodging, /nonprofits,
                /saas, each with its own /features and /pricing. Declared
                per slug (not as a `/:vertical` param) so they can't swallow
                another top-level path.
              */}
              {IS_SAAS && IS_RUMIO && VERTICALS.flatMap((v) => [
                <Route key={v.slug} path={`/${v.slug}`} element={<SaasVerticalHome vertical={v.id} />} />,
                <Route key={`${v.slug}-features`} path={`/${v.slug}/features`} element={<SaasFeatures vertical={v.id} />} />,
                <Route key={`${v.slug}-pricing`} path={`/${v.slug}/pricing`} element={<SaasPricing vertical={v.id} />} />,
              ])}
              {/* /onboarding is owned by the Rumio Site Builder app via the
                  Apps Platform mount point (path_prefix='onboarding'). The
                  previous native Questionnaire was removed once the app
                  shipped. The DB-level reserved-prefix list dropped
                  'onboarding' in migration 134 so the mount can live there. */}
              <Route path="/bundles" element={<Bundles />} />
              <Route path="/bundles/:slug" element={<BundleDetail />} />
              <Route path="/gift-cards" element={<GiftCards />} />
              <Route path="/track" element={<TrackOrder />} />
              <Route path="/track/:orderNumber" element={<TrackOrder />} />
              {/*
                Unified login/signup. Rumio platform customers (founders
                signing up for the SaaS) and tenant users (shoppers or
                admins on an ecommerce tenant) all go through the same
                SuperTokens-backed flow. The Login/Signup components pick
                the post-auth redirect based on IS_RUMIO.
              */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/*
                SuperTokens is initialised with websiteBasePath: "/auth", so
                every reset link it mints — self-serve forgot-password, admin
                provisioning, the admin reset-link action — points at
                /auth/reset-password. Only /reset-password was registered, so
                those links rendered a BLANK PAGE: the SPA booted and matched
                nothing. Password recovery was broken on every tenant.
                Both paths stay mounted, because links already sitting in
                people's inboxes carry the bare path.
              */}
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/callback" element={<MarketplaceCallback />} />
              {IS_ECOMMERCE && <Route path="/cart" element={<Cart />} />}
              {/* Public events (museum Phase 2). Route exists for all
                  tenants; admin gates visibility via the `events`
                  feature flag. */}
              <Route path="/events" element={<Suspense fallback={null}><PublicEventsList /></Suspense>} />
              <Route path="/events/:slug" element={<Suspense fallback={null}><PublicEventDetail /></Suspense>} />
              <Route path="/membership" element={<Suspense fallback={null}><PublicMembership /></Suspense>} />
              {/* Lodging public routes (STR direct booking) — gated on
                  IS_LODGING so non-lodging tenants don't accidentally route
                  to property pages that would 404 against their feature
                  flags. */}
              {/* Single-property sites have no browse list — the one property
                  is the homepage, so /properties redirects there. Multi-property
                  sites keep the list. /properties/:slug stays the canonical
                  detail URL for both (reservations, sitemap, schema.org). */}
              {IS_LODGING && <Route path="/properties" element={IS_SINGLE_PROPERTY ? <Navigate to="/" replace /> : <Suspense fallback={null}><PublicPropertiesList /></Suspense>} />}
              {IS_LODGING && <Route path="/properties/:slug" element={<Suspense fallback={null}><PublicPropertyDetail /></Suspense>} />}
              {/* Legacy full-page booking flow retired in favor of the
                  in-widget BookingModal. Redirect any stale /book links to the
                  property detail page, where the sticky widget opens the modal. */}
              {IS_LODGING && <Route path="/properties/:slug/book" element={<BookRedirect />} />}
              {IS_LODGING && <Route path="/stay/:slug" element={<Suspense fallback={null}><PublicEventLandingPage /></Suspense>} />}
              {IS_LODGING && <Route path="/reservations" element={<Suspense fallback={null}><PublicReservationLookup /></Suspense>} />}
              {IS_LODGING && <Route path="/reservations/:code" element={<Suspense fallback={null}><PublicReservationDetail /></Suspense>} />}
              {/* Cleaner portal — magic-link auth, separate identity from
                  regular users. The portal pages handle their own redirects
                  to /cleaner/login when the session token is missing. */}
              {IS_LODGING && <Route path="/cleaner/login" element={<Suspense fallback={null}><CleanerLogin /></Suspense>} />}
              {IS_LODGING && <Route path="/cleaner/verify" element={<Suspense fallback={null}><CleanerVerify /></Suspense>} />}
              {IS_LODGING && <Route path="/cleaner" element={<Suspense fallback={null}><CleanerDashboard /></Suspense>} />}
              {IS_LODGING && <Route path="/cleaner/turnovers/:id" element={<Suspense fallback={null}><CleanerTurnoverDetail /></Suspense>} />}
              {IS_LODGING && <Route path="/cleaner/history" element={<Suspense fallback={null}><CleanerHistory /></Suspense>} />}
              {IS_LODGING && <Route path="/page/things-to-do" element={<LocalGuidePage />} />}
              {/* Conventional legal URLs → the CMS pages every site ships with. */}
              <Route path="/privacy" element={<Navigate to="/page/privacy-policy" replace />} />
              <Route path="/privacy-policy" element={<Navigate to="/page/privacy-policy" replace />} />
              <Route path="/terms" element={<Navigate to="/page/terms-of-service" replace />} />
              <Route path="/terms-of-service" element={<Navigate to="/page/terms-of-service" replace />} />
              {/* Public DMCA posting. Required by 17 U.S.C. §512(c)(2) to sit
                  somewhere publicly reachable on the service — a code route so
                  it cannot be edited away or left uncreated per tenant. */}
              <Route path="/copyright" element={<Copyright />} />
              <Route path="/page/:slug" element={<DynamicPage />} />
              {mountRoutes}
              {/* Anything else: an address from the store's previous platform
                  is sent to its new home; the rest get a real "not found". */}
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Standalone pages (no layout) */}
            <Route path="/newsletter/preferences" element={<EmailPreferences />} />
            {/* In-unit welcome frame — kiosk page, token in the URL is the secret */}
            {IS_LODGING && (
              <Route
                path="/frame/:token"
                element={<Suspense fallback={null}><WelcomeFrame /></Suspense>}
              />
            )}

            {/* Checkout (minimal layout) — gated on IS_ECOMMERCE */}
            {IS_ECOMMERCE && <Route path="/checkout" element={<Checkout />} />}
            {IS_ECOMMERCE && <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />}

            {/* Account routes */}
            <Route path="/account" element={<AccountLayout />}>
              <Route index element={<AccountDashboard />} />
              {IS_ECOMMERCE && <Route path="orders" element={<AccountOrders />} />}
              {IS_ECOMMERCE && <Route path="orders/:id" element={<AccountOrderDetail />} />}
              <Route path="downloads" element={<MyDownloads />} />
              <Route path="addresses" element={<AccountAddresses />} />
              <Route path="wishlist" element={<AccountWishlist />} />
              <Route path="profile" element={<AccountProfile />} />
              <Route path="billing" element={<AccountBilling />} />
              <Route path="reviews" element={<AccountReviews />} />
              <Route path="membership" element={<Suspense fallback={null}><AccountMembership /></Suspense>} />
              {IS_LODGING && <Route path="trips" element={<Suspense fallback={null}><AccountMyTrips /></Suspense>} />}
              <Route path="loyalty" element={<Loyalty />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="returns" element={<AccountReturns />} />
              <Route path="courses" element={<MyCourses />} />
              <Route path="courses/:courseId" element={<CoursePlayer />} />
              <Route path="courses/:courseId/lesson/:lessonId" element={<CoursePlayer />} />
              <Route path="certificates" element={<Certificates />} />
            </Route>

            {/* App OAuth consent — any signed-in user, NOT admin-gated. The
                Site Builder sends customers (role=user) here mid-checkout;
                AdminLayout's admin-only guard was bouncing them to the
                landing page. The page guards sign-in itself, and the
                /apps/authorize API enforces what each role may authorize
                (admins install; users consent to already-installed apps).
                Static segments outrank AdminLayout's apps/:id for this URL. */}
            <Route path="/admin/apps/authorize" element={<AdminAppsAuthorize />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<AdminProductEditor />} />
              <Route path="products/:id" element={<AdminProductEditor />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="carts" element={<AdminCarts />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customers/:id" element={<AdminCustomerDetail />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="pages/:id" element={<AdminPageEditor />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="blog/:id" element={<AdminBlogEditor />} />
              <Route path="blog/agent" element={<AdminBlogAgent />} />
              <Route path="blog/categories" element={<AdminBlogCategories />} />
              <Route path="homepage" element={<AdminHomepageBuilder />} />
              <Route path="media" element={<AdminMediaLibrary />} />
              <Route path="design-start" element={<AdminDesignerStart />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="coupons/:id" element={<AdminCouponEditor />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="reviews/analytics" element={<AdminReviewAnalytics />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="settings/theme" element={<AdminThemeCustomizer />} />
              <Route path="settings/sandbox" element={<AdminDesignSandbox />} />
              <Route path="settings/design-history" element={<AdminDesignHistory />} />
              <Route path="settings/design-checklist" element={<AdminDesignChecklist />} />
              <Route path="settings/branding" element={<AdminBrandingStudio />} />
              <Route path="settings/email" element={<AdminEmailSetup />} />
              <Route path="settings/credentials" element={<PlatformCredentials />} />
              <Route path="settings/payments" element={<PaymentsSettings />} />
              <Route path="settings/security" element={<SecuritySettings />} />
              <Route path="settings/social-login" element={<SocialLoginSettings />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="integrations/:provider" element={<IntegrationDetail />} />
              <Route path="site-wizard" element={<SiteWizard />} />
              <Route path="site-wizard/progress/:jobId" element={<SiteWizardProgress />} />
              <Route path="promote-domain" element={<PromoteDomain />} />
              <Route path="welcome" element={<Welcome />} />
              <Route path="platform/settings" element={<PlatformSettings />} />
              <Route path="platform/plans" element={<PlatformPlans />} />
              <Route path="platform/promos" element={<PlatformPromos />} />
              <Route path="platform/store-migrations" element={<StoreMigrations />} />
              <Route path="platform/store-migrations/:id" element={<StoreMigrations />} />
              <Route path="platform/workspaces" element={<PlatformWorkspaces />} />
              <Route path="platform/workspaces/:id" element={<PlatformWorkspaceDetail />} />
              <Route path="platform/agent-fleet" element={<AgentFleetDashboard />} />
              <Route path="shipping" element={<AdminShipping />} />
              <Route path="email-popup" element={<AdminEmailPopup />} />
              <Route path="newsletter/subscribers" element={<AdminNewsletterSubscribers />} />
              <Route path="newsletter/campaigns" element={<AdminNewsletterCampaigns />} />
              <Route path="newsletter/campaigns/new" element={<AdminNewsletterCampaignEditor />} />
              <Route path="newsletter/campaigns/:id/edit" element={<AdminNewsletterCampaignEditor />} />
              <Route path="newsletter/schedules" element={<AdminNewsletterSchedules />} />
              <Route path="search-analytics" element={<AdminSearchAnalytics />} />
              <Route path="fulfillment" element={<AdminFulfillment />} />
              <Route path="fulfillment-rules" element={<AdminFulfillmentRules />} />
              <Route path="fulfillment-dashboard" element={<AdminFulfillmentDashboard />} />
              <Route path="returns" element={<AdminReturns />} />
              {/* Social casino — responsible-play self-exclusions */}
              <Route path="social-casino/self-exclusions" element={<AdminSelfExclusions />} />
              {/* Dropshipping */}
              <Route path="suppliers" element={<AdminSuppliers />} />
              <Route path="suppliers/new" element={<AdminSupplierEditor />} />
              <Route path="suppliers/:id" element={<AdminSupplierEditor />} />
              <Route path="supplier-orders" element={<AdminSupplierOrders />} />
              {/* Customs (ETaxEngine integration) */}
              <Route path="customs" element={<AdminCustomsInvoices />} />
              <Route path="customs/new" element={<AdminCustomsUpload />} />
              <Route path="customs/:id" element={<AdminCustomsDetail />} />
              {/* Marketplace */}
              <Route path="marketplace" element={<AdminMarketplaceOverview />} />
              <Route path="marketplace/channels" element={<AdminMarketplaceChannels />} />
              <Route path="marketplace/channels/new" element={<AdminMarketplaceChannelEditor />} />
              <Route path="marketplace/channels/:id" element={<AdminMarketplaceChannelEditor />} />
              <Route path="marketplace/listings" element={<AdminMarketplaceListings />} />
              <Route path="marketplace/analytics" element={<AdminMarketplaceAnalytics />} />
              {/* AI Agent */}
              <Route path="agent" element={<AgentDashboard />} />
              <Route path="agent/chat" element={<AgentChat />} />
              <Route path="agent/chat/:id" element={<AgentChat />} />
              <Route path="agent/tasks" element={<AgentTasks />} />
              <Route path="agent/tasks/:id" element={<AgentTaskDetail />} />
              <Route path="agent/config" element={<AgentConfig />} />
              <Route path="agent/business-profile" element={<BusinessProfile />} />
              <Route path="agent/notifications" element={<AgentNotifications />} />
              <Route path="agent/approvals" element={<AgentApprovals />} />
              <Route path="agent/schedules" element={<AgentSchedules />} />
              <Route path="agent/tool-stats" element={<AgentToolStats />} />
              <Route path="agent/schedules/:id" element={<AgentScheduleDetail />} />
              <Route path="agent/prospects" element={<SupplierProspects />} />
              {/* Advertising */}
              <Route path="advertising" element={<Suspense fallback={null}><AddieDashboard /></Suspense>} />
              <Route path="advertising/chat" element={<Suspense fallback={null}><AddieChat /></Suspense>} />
              <Route path="advertising/simulator" element={<Suspense fallback={null}><AddieSimulator /></Suspense>} />
              <Route path="advertising/chat/:id" element={<Suspense fallback={null}><AddieChat /></Suspense>} />
              <Route path="advertising/campaigns" element={<Suspense fallback={null}><CampaignsList /></Suspense>} />
              <Route path="advertising/campaigns/new" element={<Suspense fallback={null}><CampaignEditor /></Suspense>} />
              {/* `:id` is the read view (every asset the campaign produced); the
                  settings form it used to be now lives one level deeper. */}
              <Route path="advertising/campaigns/:id" element={<Suspense fallback={null}><CampaignFolder /></Suspense>} />
              <Route path="advertising/campaigns/:id/settings" element={<Suspense fallback={null}><CampaignEditor /></Suspense>} />
              {IS_LODGING && <Route path="advertising/local-events" element={<Suspense fallback={null}><AdLocalEvents /></Suspense>} />}
              {IS_LODGING && <Route path="advertising/review" element={<Suspense fallback={null}><AdReviewQueue /></Suspense>} />}
              {IS_LODGING && <Route path="advertising/stay-preview/:id" element={<Suspense fallback={null}><AdStayPreview /></Suspense>} />}
              <Route path="advertising/platforms" element={<Suspense fallback={null}><AdPlatforms /></Suspense>} />
              <Route path="advertising/budgets" element={<Suspense fallback={null}><BudgetManager /></Suspense>} />
              <Route path="advertising/assets" element={<Suspense fallback={null}><AssetLibrary /></Suspense>} />
              {/* Creative Studio's old home. It listed ad_creatives with no
                  campaign linkage and no detail view; the library supersedes it. */}
              <Route path="advertising/creatives" element={<Navigate to="/admin/advertising/assets?kind=image" replace />} />
              <Route path="advertising/cast" element={<Suspense fallback={null}><BrandCast /></Suspense>} />
              <Route path="advertising/cast/wizard" element={<Suspense fallback={null}><CastWizard /></Suspense>} />
              <Route path="advertising/videos" element={<Suspense fallback={null}><VideoStudio /></Suspense>} />
              <Route path="advertising/commercials" element={<Suspense fallback={null}><CommercialStudio /></Suspense>} />
              <Route path="advertising/audiences" element={<Suspense fallback={null}><Audiences /></Suspense>} />
              <Route path="advertising/funnels" element={<Suspense fallback={null}><RetargetingFunnels /></Suspense>} />
              <Route path="advertising/analytics" element={<Suspense fallback={null}><AdAnalytics /></Suspense>} />
              <Route path="advertising/tasks" element={<Suspense fallback={null}><AddieTasks /></Suspense>} />
              <Route path="advertising/schedules" element={<Suspense fallback={null}><AddieSchedules /></Suspense>} />
              <Route path="advertising/config" element={<Suspense fallback={null}><AddieConfig /></Suspense>} />
              {/* Fundraising (Penny) */}
              <Route path="fundraising" element={<Suspense fallback={null}><FundraisingDashboard /></Suspense>} />
              <Route path="fundraising/chat" element={<Suspense fallback={null}><PennyChat /></Suspense>} />
              <Route path="fundraising/chat/:id" element={<Suspense fallback={null}><PennyChat /></Suspense>} />
              <Route path="fundraising/org-profile" element={<Suspense fallback={null}><FundraisingOrgProfile /></Suspense>} />
              <Route path="fundraising/funders" element={<Suspense fallback={null}><FundraisingFunders /></Suspense>} />
              <Route path="fundraising/applications" element={<Suspense fallback={null}><FundraisingApplications /></Suspense>} />
              <Route path="fundraising/donors" element={<Suspense fallback={null}><FundraisingDonors /></Suspense>} />
              <Route path="fundraising/sponsors" element={<Suspense fallback={null}><FundraisingSponsors /></Suspense>} />
              <Route path="fundraising/members" element={<Suspense fallback={null}><FundraisingMembers /></Suspense>} />
              <Route path="fundraising/calendar" element={<Suspense fallback={null}><FundraisingCalendar /></Suspense>} />
              <Route path="fundraising/config" element={<Suspense fallback={null}><PennyConfig /></Suspense>} />
              <Route path="fundraising/membership-tiers" element={<Suspense fallback={null}><AdminMembershipTiers /></Suspense>} />
              {/* Events (museum Phase 2) */}
              <Route path="events" element={<Suspense fallback={null}><AdminEventsList /></Suspense>} />
              <Route path="events/new" element={<Suspense fallback={null}><AdminEventEditor /></Suspense>} />
              <Route path="events/:id" element={<Suspense fallback={null}><AdminEventEditor /></Suspense>} />
              <Route path="events/:id/registrations" element={<Suspense fallback={null}><AdminEventRegistrations /></Suspense>} />
              {/* Lodging admin (Phase 2) — IS_LODGING gates the routes so
                  non-lodging admins don't render Properties tabs they'd
                  never use. The backend enforces feature-flag-based access
                  control independently. */}
              {IS_LODGING && <Route path="properties" element={<Suspense fallback={null}><AdminPropertiesList /></Suspense>} />}
              {IS_LODGING && <Route path="properties/new" element={<Suspense fallback={null}><AdminPropertyEditor /></Suspense>} />}
              {IS_LODGING && <Route path="properties/:id" element={<Suspense fallback={null}><AdminPropertyEditor /></Suspense>} />}
              {IS_LODGING && <Route path="properties/:id/calendar" element={<Suspense fallback={null}><AdminPropertyCalendar /></Suspense>} />}
              {IS_LODGING && <Route path="properties-calendar" element={<Suspense fallback={null}><AdminMultiPropertyCalendar /></Suspense>} />}
              {IS_LODGING && <Route path="properties-bulk-ops" element={<Suspense fallback={null}><AdminBulkOps /></Suspense>} />}
              {IS_LODGING && <Route path="reservations" element={<Suspense fallback={null}><AdminReservationsList /></Suspense>} />}
              {IS_LODGING && <Route path="reservations/:id" element={<Suspense fallback={null}><AdminReservationDetail /></Suspense>} />}
              {IS_LODGING && <Route path="pricing-rules" element={<Suspense fallback={null}><AdminPricingRules /></Suspense>} />}
              {IS_LODGING && <Route path="channel-sync" element={<Suspense fallback={null}><AdminChannelSync /></Suspense>} />}
              {IS_LODGING && <Route path="welcome-screens" element={<Suspense fallback={null}><AdminWelcomeScreens /></Suspense>} />}
              {IS_LODGING && <Route path="lodging-reviews" element={<Suspense fallback={null}><AdminLodgingReviews /></Suspense>} />}
              {IS_LODGING && <Route path="lodging-reviews/import" element={<Suspense fallback={null}><AdminLodgingReviewImport /></Suspense>} />}
              {IS_LODGING && <Route path="lodging" element={<Suspense fallback={null}><AdminLodgingDashboard /></Suspense>} />}
              {IS_LODGING && <Route path="messages" element={<Suspense fallback={null}><AdminMessagesInbox /></Suspense>} />}
              {IS_LODGING && <Route path="turnovers" element={<Suspense fallback={null}><AdminTurnovers /></Suspense>} />}
              {IS_LODGING && <Route path="cleaners" element={<Suspense fallback={null}><AdminCleaners /></Suspense>} />}
              {IS_LODGING && <Route path="ownerrez" element={<Suspense fallback={null}><AdminOwnerRez /></Suspense>} />}
              {/* Courses */}
              <Route path="courses" element={<AdminCourses />} />
              <Route path="courses/new" element={<AdminCourseEditor />} />
              <Route path="courses/:id" element={<AdminCourseEditor />} />
              <Route path="enrollments" element={<AdminEnrollments />} />
              <Route path="subscription-plans" element={<AdminSubscriptionPlans />} />
              <Route path="subscription-promos" element={<AdminSubscriptionPromos />} />
              <Route path="founders-pricing" element={<AdminFoundersPricing />} />
              <Route path="apps" element={<AdminApps />} />
              {/* apps/authorize deliberately lives OUTSIDE this admin tree —
                  customers (role=user) reach the consent page mid-checkout
                  from the Site Builder; see the standalone route above. */}
              <Route path="apps/:id" element={<AdminAppDetail />} />
              {/* Settings */}
              <Route path="feature-toggles" element={<FeatureToggles />} />
              {/* Growth & Upsells */}
              <Route path="upsell" element={<UpsellDashboard />} />
              <Route path="upsell/associations" element={<UpsellAssociations />} />
              <Route path="upsell/volume-discounts" element={<UpsellVolumeDiscounts />} />
              <Route path="upsell/bundles" element={<UpsellBundles />} />
              <Route path="upsell/bundles/analytics" element={<UpsellBundleAnalytics />} />
              <Route path="upsell/bundles/new" element={<UpsellBundleEditor />} />
              <Route path="upsell/bundles/:id/edit" element={<UpsellBundleEditor />} />
              <Route path="upsell/timers" element={<UpsellTimers />} />
              <Route path="upsell/email-automation" element={<UpsellEmailAutomation />} />
              <Route path="upsell/loyalty" element={<UpsellLoyalty />} />
              <Route path="upsell/referrals" element={<UpsellReferrals />} />
              <Route path="upsell/gift-cards" element={<UpsellGiftCards />} />
              {/* Several admin routes are compiled out per site type (the
                  lodging-only advertising review queue, stay previews, local
                  events). Without a catch-all those render a blank page on
                  every other tenant instead of going somewhere useful. */}
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>
          </Routes>
  );
}

export default function App() {
  // First-touch campaign attribution (utm_*, mc_cid, gclid, …) for checkout's utm_data.
  useEffect(() => {
    captureAttribution();
  }, []);
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppInitializer>
            <ScrollToTop />
            <RuntimeBrand />
            <DesignPreviewBridge />
            {/* Top-level shared preview link (?design_share=…). The bridge above
                only ever activates inside the sandbox's own iframe. */}
            <DesignSharePreview />
            <LocaleResolver />
            <ToastProvider />
            <UpdateAvailableBanner />
            <RouteErrorBoundary>
              <MaintenanceGuard>
                <AuthorizedHomeRedirect>
                  <RoutesWithMounts />
                </AuthorizedHomeRedirect>
              </MaintenanceGuard>
            </RouteErrorBoundary>
          </AppInitializer>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
